import React, { useState, useEffect } from 'react';
import api from './api';
import { useNavigate } from 'react-router-dom';

// --- RATE INPUT COMPONENT (MOVED OUTSIDE) ---
const RateInput = ({ proc, size, partyForm, setPartyForm }) => (
    <div className="col-4 mb-2">
        <label className="small text-muted" style={{fontSize: '0.75rem'}}>{size}</label>
        <input 
          type="number" className="form-control form-control-sm" 
          placeholder="₹"
          value={partyForm.rates?.[proc]?.[size] ?? ''}
          onChange={e => {
              const val = parseFloat(e.target.value);
              setPartyForm(prev => ({
                  ...prev,
                  rates: {
                      ...prev.rates,
                      [proc]: {
                          ...prev.rates[proc],
                          [size]: isNaN(val) ? 0 : val
                      }
                  }
              }));
          }}
        />
    </div>
);

const AutoJobWork = () => {
  const navigate = useNavigate();
  
  // Constants
  const SIZES = ['0-2.70', '2.71-3.00', '3.01-3.30', '3.31-3.60', '3.61-4.00', '4.01-10.00'];
  const PROCESSES = ['Auto Bottom', 'Auto Pel'];

  // State
  const [activeTab, setActiveTab] = useState('entry'); 
  const [parties, setParties] = useState([]);
  const [entries, setEntries] = useState([]);
  
  // -- ENTRY FORM STATE --
  const initialWorkState = SIZES.reduce((acc, size) => ({ ...acc, [size]: '' }), {});
  
  const [entryForm, setEntryForm] = useState({
      date: new Date().toISOString().split('T')[0],
      partyId: '',
      process: 'Auto Bottom',
      work: initialWorkState
  });
  const [calculatedCost, setCalculatedCost] = useState(0);

  // -- MASTER FORM STATE --
  const createEmptyRates = () => {
      const empty = {};
      PROCESSES.forEach(proc => {
          empty[proc] = {};
          SIZES.forEach(size => empty[proc][size] = 0);
      });
      return empty;
  };

  const [editPartyId, setEditPartyId] = useState(null); 
  const [partyForm, setPartyForm] = useState({
      name: '',
      mobile: '',
      rates: createEmptyRates()
  });

  // -- BILL STATE --
  const [billFilter, setBillFilter] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear(), partyId: '' });
  const [billData, setBillData] = useState([]);

  useEffect(() => {
      fetchParties();
      fetchEntries();
  }, []);

  // --- API CALLS ---
  const fetchParties = async () => {
      try {
          const res = await api.get('/auto/job/parties');
          setParties(res.data);
      } catch (err) { console.error("Error fetching parties:", err); }
  };
  const fetchEntries = async () => {
      try {
          const res = await api.get('/auto/job/entries');
          setEntries(res.data);
      } catch (err) { console.error("Error fetching entries:", err); }
  };

  // --- CALCULATION LOGIC ---
  useEffect(() => {
      if (!entryForm.partyId) { setCalculatedCost(0); return; }
      
      const party = parties.find(p => p._id === entryForm.partyId);
      
      if (!party || !party.rates) { setCalculatedCost(0); return; }

      let total = 0;
      SIZES.forEach(size => {
          const pcs = parseFloat(entryForm.work[size]) || 0;
          const processRates = party.rates[entryForm.process] || {}; 
          const rate = parseFloat(processRates[size]) || 0; 
          total += (pcs * rate);
      });
      setCalculatedCost(total);

  }, [entryForm, parties]);

  // --- HANDLERS ---
  const handleEntrySubmit = async (e) => {
      e.preventDefault();
      if (!entryForm.partyId) return alert("Select Party");

      try {
          // 1. CLEAN DATA: Convert empty strings to 0
          const cleanWork = {};
          let totalPcs = 0;
          
          Object.keys(entryForm.work).forEach(key => {
              const val = parseFloat(entryForm.work[key]);
              // If it's a valid number, keep it. If empty/NaN, set to 0.
              const cleanVal = isNaN(val) ? 0 : val;
              
              cleanWork[key] = cleanVal;
              totalPcs += cleanVal;
          });

          // 2. SEND CLEAN DATA
          await api.post('/auto/job/entries', { 
              ...entryForm, 
              work: cleanWork, // Send the cleaned object (Numbers only)
              totalPcs, 
              totalAmount: calculatedCost 
          });

          alert("Work Saved!");
          setEntryForm({ ...entryForm, work: initialWorkState });
          fetchEntries();
      } catch (err) {
          console.error(err);
          alert("Error saving work: " + (err.response?.data?.error || err.message));
      }
  };

  const handleDeleteEntry = async (id) => {
      if(!window.confirm("Delete this entry?")) return;
      try {
          await api.delete(`/auto/job/entries/${id}`);
          fetchEntries();
      } catch (err) { alert("Error deleting"); }
  };

  const handlePartySubmit = async (e) => {
      e.preventDefault();
      if (!partyForm.name) return alert("Enter Name");
      
      try {
          if (editPartyId) {
              await api.put(`/auto/job/parties/${editPartyId}`, partyForm);
              alert("Party Updated!");
          } else {
              await api.post('/auto/job/parties', partyForm);
              alert("Party Added!");
          }
          resetPartyForm();
          fetchParties();
      } catch (err) {
          alert("Error saving party: " + (err.response?.data?.error || err.message));
      }
  };

  const handleEditParty = (party) => {
      setEditPartyId(party._id);
      const loadedRates = createEmptyRates();
      
      if (party.rates) {
          PROCESSES.forEach(proc => {
              if (party.rates[proc]) {
                  SIZES.forEach(size => {
                      if (party.rates[proc][size] !== undefined) {
                          loadedRates[proc][size] = party.rates[proc][size];
                      }
                  });
              }
          });
      }

      setPartyForm({
          name: party.name,
          mobile: party.mobile || '',
          rates: loadedRates
      });
      
      const formElement = document.getElementById('partyFormTop');
      if(formElement) formElement.scrollIntoView({ behavior: 'smooth' });
  };

  const resetPartyForm = () => {
      setEditPartyId(null);
      setPartyForm({ name: '', mobile: '', rates: createEmptyRates() });
  };

  const handleBillGenerate = async () => {
      try {
          const res = await api.get('/auto/job/bill', { params: billFilter });
          setBillData(res.data);
      } catch (err) { alert("Error generating bill: " + err.message); }
  };

  // Helper to find party details for bill header
  const currentParty = parties.find(p => p._id === billFilter.partyId);

  return (
    <div className="container mt-4 mb-5">
      
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4 d-print-none">
          <h2 className="text-primary fw-bold">🏭 Auto Job Work</h2>
          <button className="btn btn-secondary" onClick={() => navigate('/')}>Dashboard</button>
      </div>

      {/* TABS */}
      <ul className="nav nav-tabs mb-4 d-print-none">
          <li className="nav-item"><button className={`nav-link ${activeTab==='entry'?'active fw-bold':''}`} onClick={()=>setActiveTab('entry')}>📝 Daily Entry</button></li>
          <li className="nav-item"><button className={`nav-link ${activeTab==='bill'?'active fw-bold':''}`} onClick={()=>setActiveTab('bill')}>🧾 Monthly Bill</button></li>
          <li className="nav-item"><button className={`nav-link ${activeTab==='master'?'active fw-bold':''}`} onClick={()=>setActiveTab('master')}>⚙️ Party & Rates</button></li>
      </ul>

      {/* --- TAB 1: DAILY ENTRY --- */}
      {activeTab === 'entry' && (
          <div className="row">
              <div className="col-md-4">
                  <div className="card shadow-sm p-3 border-primary">
                      <h5 className="mb-3">Add Work</h5>
                      <form onSubmit={handleEntrySubmit}>
                          <div className="mb-2">
                              <label>Date</label>
                              <input type="date" className="form-control" value={entryForm.date} onChange={e => setEntryForm({...entryForm, date: e.target.value})} />
                          </div>
                          <div className="mb-2">
                              <label>Party</label>
                              <select className="form-select" value={entryForm.partyId} onChange={e => setEntryForm({...entryForm, partyId: e.target.value})}>
                                  <option value="">-- Select --</option>
                                  {parties.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                              </select>
                          </div>
                          <div className="mb-3">
                              <label>Process</label>
                              <select className="form-select" value={entryForm.process} onChange={e => setEntryForm({...entryForm, process: e.target.value})}>
                                  {PROCESSES.map(p => <option key={p} value={p}>{p}</option>)}
                              </select>
                          </div>
                          
                          <h6 className="fw-bold text-muted border-bottom pb-1">Pieces per Size</h6>
                          <div className="row g-2 mb-3">
                              {SIZES.map(size => (
                                  <div key={size} className="col-6">
                                      <label className="small">{size}</label>
                                      <input 
                                        type="number" className="form-control" 
                                        value={entryForm.work[size]} 
                                        onChange={e => setEntryForm({...entryForm, work: {...entryForm.work, [size]: e.target.value}})} 
                                      />
                                  </div>
                              ))}
                          </div>

                          <div className="alert alert-info py-2 text-center fw-bold">
                              Est. Bill: ₹{calculatedCost.toLocaleString()}
                          </div>

                          <button className="btn btn-primary w-100">Save Entry</button>
                      </form>
                  </div>
              </div>

              <div className="col-md-8">
                  <div className="card shadow-sm">
                      <div className="card-header bg-light fw-bold">Recent Entries (Last 50)</div>
                      <div className="table-responsive">
                          <table className="table table-sm table-hover align-middle mb-0 text-center" style={{fontSize:'12px'}}>
                              <thead className="table-light">
                                  <tr><th>Date</th><th>Party</th><th>Process</th><th>Total Pcs</th><th>Amount</th><th>Action</th></tr>
                              </thead>
                              <tbody>
                                  {entries.map(e => (
                                      <tr key={e._id}>
                                          <td>{new Date(e.date).toLocaleDateString()}</td>
                                          <td className="fw-bold text-primary">{e.partyId?.name}</td>
                                          <td>{e.process}</td>
                                          <td>{e.totalPcs}</td>
                                          <td className="fw-bold text-success">₹{e.totalAmount}</td>
                                          <td><button className="btn btn-sm text-danger" onClick={() => handleDeleteEntry(e._id)}>✖</button></td>
                                      </tr>
                                  ))}
                              </tbody>
                          </table>
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* --- TAB 2: MONTHLY BILL --- */}
      {activeTab === 'bill' && (
          <div>
              <div className="card shadow-sm p-3 mb-4 d-print-none">
                  <div className="d-flex gap-3 align-items-end">
                      <div>
                          <label className="small fw-bold">Month</label>
                          <select className="form-select" value={billFilter.month} onChange={e => setBillFilter({...billFilter, month: e.target.value})}>
                              {Array.from({length: 12}, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}</option>)}
                          </select>
                      </div>
                      <div>
                          <label className="small fw-bold">Year</label>
                          <select className="form-select" value={billFilter.year} onChange={e => setBillFilter({...billFilter, year: e.target.value})}>
                              <option value="2024">2024</option><option value="2025">2025</option><option value="2026">2026</option>
                          </select>
                      </div>
                      <div style={{minWidth: '200px'}}>
                          <label className="small fw-bold">Party</label>
                          <select className="form-select" value={billFilter.partyId} onChange={e => setBillFilter({...billFilter, partyId: e.target.value})}>
                              <option value="">All Parties</option>
                              {parties.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                          </select>
                      </div>
                      <button className="btn btn-success fw-bold" onClick={handleBillGenerate}>Generate</button>
                      <button className="btn btn-outline-dark" onClick={() => window.print()}>🖨️ Print</button>
                  </div>
              </div>

              {billData.length > 0 ? (
                  <div className="print-container bg-white p-4">
                      {/* PRINT STYLES - A5 OPTIMIZED */}
                      <style>
                        {`
                            @media print {
                                @page { size: A5 portrait; margin: 8mm; }
                                body { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 9pt; color: #000; background: #fff; }
                                .d-print-none { display: none !important; }
                                .print-container { width: 100%; max-width: 100%; margin: 0 auto; padding: 0 !important; }
                                
                                /* Header Styling Compact */
                                .invoice-header { border-bottom: 2px solid #000; padding-bottom: 5px; margin-bottom: 10px; display: flex; justify-content: space-between; }
                                .company-title { font-size: 14pt; font-weight: 900; letter-spacing: 1px; margin: 0; text-transform: uppercase; }
                                .company-address { font-size: 8pt; line-height: 1.2; color: #333; margin-top: 2px; }
                                .invoice-tag { text-align: right; }
                                .tag-main { font-size: 12pt; font-weight: bold; text-transform: uppercase; color: #444; }
                                .tag-sub { font-size: 8pt; margin-top: 2px; }

                                /* Info Box Compact */
                                .info-box { display: flex; justify-content: space-between; margin-bottom: 10px; border: 1px solid #ccc; padding: 5px; }
                                .info-col { width: 48%; }
                                .info-label { font-size: 7pt; color: #666; text-transform: uppercase; font-weight: bold; margin-bottom: 2px; }
                                .info-value { font-size: 10pt; font-weight: bold; }

                                /* Table Styling Compact */
                                table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
                                th, td { border: 1px solid #999; padding: 3px 2px; text-align: center; font-size: 7.5pt; }
                                th { background-color: #f0f0f0 !important; font-weight: bold; text-transform: uppercase; border-bottom: 1px solid #000; }
                                .text-left { text-align: left; padding-left: 4px; }
                                tr { page-break-inside: avoid; }

                                /* Footer Compact */
                                .invoice-footer { margin-top: 20px; display: flex; justify-content: space-between; page-break-inside: avoid; }
                                .sign-box { width: 30%; text-align: center; border-top: 1px solid #000; padding-top: 2px; font-weight: bold; font-size: 8pt; }
                            }
                        `}
                      </style>

                      {/* HEADER SECTION */}
                      <div className="invoice-header">
                          <div>
                              <div className="company-title">VEKARIYA GEMS</div>
                              <div className="company-address">
                                  First Floor, Safal House, Plot no. 22A & 22B,<br/>
                                  Mahatma Industrial Co.Op Service Society V-2,<br/>
                                  Varachha Road, Surat - 395006.<br/>
                                  <strong>GSTIN: 24AALFV5724D1Z7</strong>
                              </div>
                          </div>
                          <div className="invoice-tag">
                              <div className="tag-main">JOB STATEMENT</div>
                              <div className="tag-sub">Period: <strong>{billFilter.month}/{billFilter.year}</strong></div>
                              <div className="tag-sub">Date: {new Date().toLocaleDateString('en-GB')}</div>
                          </div>
                      </div>

                      {/* BILL TO SECTION */}
                      {currentParty && (
                          <div className="info-box">
                              <div className="info-col">
                                  <div className="info-label">BILL TO PARTY</div>
                                  <div className="info-value">{currentParty.name}</div>
                                  <div style={{fontSize:'8pt'}}>{currentParty.mobile || ''}</div>
                              </div>
                              <div className="info-col text-end">
                                  <div className="info-label">TOTAL PAYABLE</div>
                                  <div className="info-value" style={{fontSize:'12pt'}}>₹ {billData.reduce((sum, b) => sum + b.totalAmount, 0).toLocaleString()}</div>
                              </div>
                          </div>
                      )}

                      {/* TABLE */}
                      <table className="table table-bordered text-center align-middle">
                          <thead>
                              <tr>
                                  <th style={{width:'12%'}}>Date</th>
                                  {!currentParty && <th style={{width:'15%'}}>Party</th>}
                                  <th style={{width:'15%'}}>Process</th>
                                  {SIZES.map(s => <th key={s}>{s}</th>)}
                                  <th style={{width:'8%'}}>Total</th>
                                  <th style={{width:'12%'}}>Amt (₹)</th>
                              </tr>
                          </thead>
                          <tbody>
                              {billData.map(b => (
                                  <tr key={b._id}>
                                      <td>{new Date(b.date).toLocaleDateString('en-GB')}</td>
                                      {!currentParty && <td className="text-left fw-bold">{b.partyId?.name}</td>}
                                      <td className="text-left">{b.process}</td>
                                      {SIZES.map(s => <td key={s} className="text-muted">{b.work[s] || '-'}</td>)}
                                      <td className="fw-bold">{b.totalPcs}</td>
                                      <td className="fw-bold">₹{b.totalAmount}</td>
                                  </tr>
                              ))}
                          </tbody>
                          <tfoot>
                              <tr style={{background:'#eee', fontWeight:'bold', borderTop:'1px solid #000'}}>
                                  <td colSpan={currentParty ? 2 + SIZES.length : 3 + SIZES.length} className="text-end" style={{paddingRight:'10px'}}>GRAND TOTAL</td>
                                  <td style={{fontSize:'9pt'}}>{billData.reduce((sum, b) => sum + b.totalPcs, 0)}</td>
                                  <td style={{fontSize:'9pt'}}>₹{billData.reduce((sum, b) => sum + b.totalAmount, 0).toLocaleString()}</td>
                              </tr>
                          </tfoot>
                      </table>

                      {/* SIGNATURES */}
                      <div className="invoice-footer">
                          <div className="sign-box">Checked By</div>
                          <div className="sign-box">Receiver's Signature</div>
                          <div className="sign-box">Authorised Signature</div>
                      </div>
                  </div>
              ) : (
                  <div className="text-center p-5 text-muted">No records found. Click Generate.</div>
              )}
          </div>
      )}

      {/* --- TAB 3: PARTY MASTER (RATES) --- */}
      {activeTab === 'master' && (
          <div className="row">
              <div className="col-md-5">
                  <div id="partyFormTop" className={`card shadow-sm border-${editPartyId ? 'warning' : 'primary'}`}>
                      <div className={`card-header ${editPartyId ? 'bg-warning text-dark' : 'bg-primary text-white'} fw-bold`}>
                          {editPartyId ? 'Edit Party & Rates' : 'Add New Party'}
                      </div>
                      <div className="card-body">
                          <div className="row g-2 mb-3">
                              <div className="col-8">
                                  <label className="small">Party Name</label>
                                  <input className="form-control" value={partyForm.name} onChange={e => setPartyForm({...partyForm, name: e.target.value})} placeholder="e.g. Radhe Gems" />
                              </div>
                              <div className="col-4">
                                  <label className="small">Mobile</label>
                                  <input className="form-control" value={partyForm.mobile} onChange={e => setPartyForm({...partyForm, mobile: e.target.value})} />
                              </div>
                          </div>

                          <div className="row">
                              <div className="col-6 border-end">
                                  <h6 className="text-primary fw-bold small">Auto Bottom (₹)</h6>
                                  <div className="row g-1">
                                      {SIZES.map(s => (
                                          <RateInput 
                                            key={s} 
                                            proc="Auto Bottom" 
                                            size={s} 
                                            partyForm={partyForm}
                                            setPartyForm={setPartyForm}
                                          />
                                      ))}
                                  </div>
                              </div>
                              <div className="col-6">
                                  <h6 className="text-danger fw-bold small">Auto Pel (₹)</h6>
                                  <div className="row g-1">
                                      {SIZES.map(s => (
                                          <RateInput 
                                            key={s} 
                                            proc="Auto Pel" 
                                            size={s} 
                                            partyForm={partyForm}
                                            setPartyForm={setPartyForm}
                                          />
                                      ))}
                                  </div>
                              </div>
                          </div>

                          <div className="d-flex gap-2 mt-4">
                              {editPartyId && <button className="btn btn-outline-secondary w-50" onClick={resetPartyForm}>Cancel</button>}
                              <button className={`btn w-100 fw-bold ${editPartyId ? 'btn-warning' : 'btn-primary'}`} onClick={handlePartySubmit}>
                                  {editPartyId ? 'Update Rates' : 'Save Party'}
                              </button>
                          </div>
                      </div>
                  </div>
              </div>

              <div className="col-md-7">
                  <div className="card shadow-sm">
                      <div className="card-header bg-light fw-bold">Existing Parties ({parties.length})</div>
                      <div className="list-group list-group-flush" style={{maxHeight: '500px', overflowY: 'auto'}}>
                          {parties.map(p => (
                              <div key={p._id} className="list-group-item d-flex justify-content-between align-items-center">
                                  <div>
                                      <div className="fw-bold text-primary">{p.name}</div>
                                      <div className="small text-muted">{p.mobile || 'No Mobile'}</div>
                                  </div>
                                  <div className="text-end">
                                      <button className="btn btn-sm btn-outline-warning" onClick={() => handleEditParty(p)}>✏️ Edit Rates</button>
                                  </div>
                              </div>
                          ))}
                          {parties.length === 0 && <div className="p-3 text-center text-muted">No parties added yet.</div>}
                      </div>
                  </div>
              </div>
          </div>
      )}

    </div>
  );
};

export default AutoJobWork;